import React, { useState, useRef, useEffect } from "react";
import {
  Save,
  Loader2,
  ExternalLink,
  Calendar,
  X,
  ChevronDown,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SplitButton } from "@/components/ui/split-button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "../ui/input";
import { getSlug } from "@/lib/utils";
import { ArticlePost } from "@/types/article";
import { PostsStatusOptions } from "@/types/pocketbase-types";
import { startOfTomorrow } from "date-fns";

interface EditorHeaderProps {
  title: string;
  onBack: () => void;
  publishDisabled: boolean;
  onSave?: () => void;
  onPublish?: () => void;
  onScheduleWithDate?: (date: Date) => void;
  onUnschedule?: () => void;
  onTitleChange?: (data: Partial<ArticlePost>) => void;
  isSaving?: boolean;
  isPublishing?: boolean;
  isPublished?: boolean;
  publishedUrl?: string;
  postStatus?: PostsStatusOptions;
  scheduleDate?: string;
}

export const EditorHeader: React.FC<EditorHeaderProps> = ({
  title,
  onSave,
  onPublish,
  onScheduleWithDate,
  onUnschedule,
  onBack,
  onTitleChange,
  publishDisabled = true,
  isSaving = false,
  isPublishing = false,
  isPublished = false,
  publishedUrl,
  postStatus,
  scheduleDate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditableTitle(title);
  }, [title]);

  const handleScheduleForLater = () => {
    // Pre-populate with current scheduled date if rescheduling
    if (postStatus === PostsStatusOptions.Scheduled && scheduleDate) {
      setSelectedDate(new Date(scheduleDate));
    }
    setShowDatePicker(true);
  };

  const handleDateChange = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date && onScheduleWithDate) {
      onScheduleWithDate(date);
      setShowDatePicker(false);
      setSelectedDate(undefined);
    }
  };

  const handleCancelScheduling = () => {
    setShowDatePicker(false);
    setSelectedDate(undefined);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleTitleClick = () => {
    if (onTitleChange) {
      setIsEditing(true);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableTitle(e.target.value);
  };

  const handleTitleBlur = () => {
    setIsEditing(false);
    if (onTitleChange && editableTitle.trim() !== "") {
      onTitleChange({
        title: editableTitle,
        seoTitle: editableTitle,
        slug: getSlug(editableTitle),
      });
    } else {
      setEditableTitle(title); // Reset to original if empty
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      setEditableTitle(title);
      setIsEditing(false);
    }
  };
  return (
    <div className="border-b border-gray-200 px-4 py-3 sticky top-0 z-10 bg-white">
      <div className="flex items-center mb-2">
        <Button
          variant="link"
          size="sm"
          className="flex items-center text-blue-400 hover:text-blue-500 h-6 px-0"
          onClick={onBack}
        >
          ← Back
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div
          className="flex-grow mr-4"
          style={{ minWidth: isEditing ? "500px" : "auto", maxWidth: "80%" }}
        >
          {isEditing ? (
            <Input
              ref={inputRef}
              type="text"
              value={editableTitle}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              onKeyDown={handleKeyDown}
              className="w-full text-xl font-medium py-1"
              style={{ minWidth: "100%" }}
              autoFocus
            />
          ) : (
            <h2
              className="text-xl font-medium text-gray-900 cursor-pointer hover:text-gray-700"
              onClick={handleTitleClick}
              title={onTitleChange ? "Click to edit title" : ""}
            >
              {editableTitle}
            </h2>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="default"
            size="sm"
            onClick={onSave}
            className="flex items-center"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="mr-1.5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-1.5" />
                Save
              </>
            )}
          </Button>

          {isPublished && publishedUrl ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.open(publishedUrl, "_blank")}
            >
              <ExternalLink size={16} className="mr-1.5" />
              View published article
            </Button>
          ) : postStatus === PostsStatusOptions.Scheduled && scheduleDate ? (
            <div className="relative">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-blue-600 font-medium flex items-center p-0 h-auto"
                    disabled={isPublishing}
                  >
                    <Calendar size={14} className="mr-1" />
                    Scheduled for {new Date(scheduleDate).toLocaleDateString()}
                    <ChevronDown size={14} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={handleScheduleForLater}
                    disabled={isPublishing}
                    className="flex items-center"
                  >
                    <Calendar size={14} className="mr-2" />
                    Change Date
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={onUnschedule}
                    disabled={isPublishing}
                    className="flex items-center"
                  >
                    <X size={14} className="mr-2" />
                    Unschedule
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Inline DatePicker for rescheduling */}
              {showDatePicker && (
                <div className="absolute top-full right-0 mt-1 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      Select publication date
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelScheduling}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                  <DatePicker
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                    placeholder="Select a date"
                    minDate={startOfTomorrow()}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <SplitButton
                mainButtonText={isPublishing ? "Publishing..." : "Publish Now"}
                mainButtonIcon={!isPublishing ? <Send size={16} /> : undefined}
                onMainClick={onPublish}
                mainButtonProps={{
                  variant: "outline",
                  size: "sm",
                  disabled: isPublishing || publishDisabled,
                }}
                dropdownItems={[
                  {
                    key: "schedule",
                    label: "Schedule for Later",
                    onClick: handleScheduleForLater,
                    icon: <Calendar size={10} />,
                    disabled: isPublishing,
                  },
                ]}
                disabled={isPublishing || publishDisabled}
              />

              {/* Inline DatePicker for initial scheduling */}
              {showDatePicker && (
                <div className="absolute top-full right-0 mt-1 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      Publication date
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelScheduling}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                  <DatePicker
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                    placeholder="Select a date"
                    minDate={startOfTomorrow()}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
